var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { registerAutomotive } from '../../registryCore';
import { bool, createAutoGroup, num, setParts, setRefresh, setState, str, } from '../../helpers';
import { themeFromProps } from '../../themes';
import { autoCenteredText, centerInBounds, fitTextX, fluidFont, resolveBounds } from '../../layout';
import { buildLampWidget } from '../../primitives/builders';
registerAutomotive('gearIndicator', function (props, app) {
    var gear = str(props, 'gear', 'P');
    var theme = themeFromProps(props);
    var bounds = resolveBounds(props, 56, 60);
    var group = createAutoGroup(app, 'gearIndicator', __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), 'gearIndicator');
    var w = bounds.innerWidth;
    var h = bounds.innerHeight;
    group.add(app.roundedRect({
        x: bounds.pad,
        y: bounds.pad,
        width: w,
        height: h,
        cornerRadius: 8,
        fill: '#111827',
        stroke: theme.dialStroke,
        strokeWidth: 2,
        listening: false,
    }));
    var label = autoCenteredText(app, gear, w, h / 2, {
        fontSize: fluidFont(36, bounds, 18, 40),
        fontWeight: 'bold',
        fill: theme.text,
        insetX: bounds.pad,
        insetY: bounds.pad,
    });
    group.add(label);
    setParts(group, { label: label });
    group.metadata.textRefresh = function (t) {
        label.text = t;
    };
    setState(group, { gear: gear, width: bounds.width, height: bounds.height });
    return group;
});
registerAutomotive('turnIndicators', function (props, app) {
    var left = bool(props, 'left', false);
    var right = bool(props, 'right', false);
    var theme = themeFromProps(props);
    var bounds = resolveBounds(props, 56, 28);
    var group = createAutoGroup(app, 'turnIndicators', __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), 'turnIndicators');
    var pad = bounds.pad;
    var panelW = bounds.innerWidth;
    var panelH = bounds.innerHeight;
    var compact = panelH < 18 || panelW < 40;
    var innerPad = Math.max(compact ? 2 : 4, Math.round(Math.min(panelW, panelH) * 0.1));
    var availW = panelW - innerPad * 2;
    var gap = Math.max(compact ? 3 : 5, availW * 0.14);
    var arrowW = Math.max(compact ? 7 : 10, (availW - gap) / 2);
    var arrowH = Math.max(compact ? 5 : 8, Math.min(panelH - innerPad * 2, panelH * (compact ? 0.62 : 0.52)));
    var totalW = arrowW * 2 + gap;
    var startX = pad + innerPad + Math.max(0, (panelW - innerPad * 2 - totalW) / 2);
    var cy = pad + panelH / 2;
    var leftX = startX;
    var rightX = startX + arrowW + gap;
    var onColor = theme.lampOn;
    var offColor = theme.lampOff;
    var onStroke = '#fbbf24';
    var offStroke = theme.dialStroke;
    var arrowPoints = function (x, flip) {
        return flip
            ? [x, cy, x + arrowW, cy - arrowH / 2, x + arrowW, cy + arrowH / 2]
            : [x + arrowW, cy, x, cy - arrowH / 2, x, cy + arrowH / 2];
    };
    var arrowStyle = function (on) { return ({
        fill: on ? onColor : offColor,
        stroke: on ? onStroke : offStroke,
        strokeWidth: 1,
        shadow: on ? { color: 'rgba(251,191,36,0.4)', blur: compact ? 4 : 6, offsetX: 0, offsetY: 0 } : undefined,
        listening: false,
    }); };
    group.add(app.roundedRect({
        x: pad,
        y: pad,
        width: panelW,
        height: panelH,
        cornerRadius: Math.min(6, panelH * 0.2),
        fill: '#111827',
        stroke: theme.dialStroke,
        strokeWidth: compact ? 1 : 1.5,
        listening: false,
    }));
    var leftShape = app.polygon(__assign({ points: arrowPoints(leftX, false) }, arrowStyle(left)));
    var rightShape = app.polygon(__assign({ points: arrowPoints(rightX, true) }, arrowStyle(right)));
    group.add(leftShape, rightShape);
    var applyState = function (l, r) {
        leftShape.fill = l ? onColor : offColor;
        rightShape.fill = r ? onColor : offColor;
        leftShape.stroke = l ? onStroke : offStroke;
        rightShape.stroke = r ? onStroke : offStroke;
        leftShape.shadow = l ? { color: 'rgba(251,191,36,0.4)', blur: compact ? 4 : 6, offsetX: 0, offsetY: 0 } : null;
        rightShape.shadow = r ? { color: 'rgba(251,191,36,0.4)', blur: compact ? 4 : 6, offsetX: 0, offsetY: 0 } : null;
        leftShape.markDirty();
        rightShape.markDirty();
    };
    group.metadata.refresh = function (l, r) { return applyState(l, r); };
    setParts(group, { leftShape: leftShape, rightShape: rightShape });
    setState(group, { left: left, right: right, width: bounds.width, height: bounds.height });
    return group;
});
registerAutomotive('parkingBrake', function (props, app) { return buildLampWidget(app, 'parkingBrake', 'parkingBrake', props, 'P'); });
registerAutomotive('headlights', function (props, app) { return buildLampWidget(app, 'headlights', 'headlights', props, 'HL'); });
/** Amber MIL / check-engine lamp (ISO 2575 style amber telltale). */
registerAutomotive('checkEngineLamp', function (props, app) {
    return buildLampWidget(app, 'checkEngineLamp', 'checkEngineLamp', props, 'MIL', {
        onFill: '#f59e0b',
        onStroke: '#fbbf24',
        onText: '#111',
        onShadow: 'rgba(245,158,11,0.55)',
    });
});
registerAutomotive('cruiseControl', function (props, app) {
    var speed = num(props, 'speed', 0);
    var active = bool(props, 'active', speed > 0);
    var bounds = resolveBounds(props, 80, 32);
    var group = createAutoGroup(app, 'cruiseControl', __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), 'cruiseControl');
    var w = bounds.innerWidth;
    var h = bounds.innerHeight;
    var bg = app.roundedRect({ width: w, height: h, cornerRadius: 4, fill: active ? '#1d4ed8' : '#333', listening: false });
    var label = autoCenteredText(app, active ? "SET ".concat(Math.round(speed)) : 'CRUISE', w, h / 2, {
        fontSize: fluidFont(11, bounds, 8, 12),
        fontWeight: 'bold',
        fill: '#fff',
    });
    group.add(bg, label);
    setParts(group, { bg: bg, label: label });
    setRefresh(group, function (v) {
        var on = v > 0;
        bg.fill = on ? '#1d4ed8' : '#333';
        label.text = on ? "SET ".concat(Math.round(v)) : 'CRUISE';
    });
    setState(group, { speed: speed, active: active, width: bounds.width, height: bounds.height });
    return group;
});
registerAutomotive('warningLamp', function (props, app) {
    var labelText = str(props, 'label', '!');
    var active = bool(props, 'active', false);
    var bounds = resolveBounds(props, 36, 36);
    var group = createAutoGroup(app, 'warningLamp', __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), 'warningLamp');
    var radius = Math.min(bounds.innerWidth, bounds.innerHeight) / 2 - 3;
    var maxR = Math.max(12, Math.min(radius, 56));
    var center = centerInBounds(bounds, maxR * 2, maxR * 2);
    var symSize = fluidFont(10, bounds, 8, 12);
    group.add(app.circle({ radius: maxR, x: center.x, y: center.y, fill: active ? '#ef4444' : '#333', stroke: active ? '#fca5a5' : '#555', strokeWidth: 1, listening: false }), app.text({
        text: labelText,
        x: center.x + fitTextX(labelText, symSize, maxR * 2),
        y: center.y + maxR,
        fontSize: symSize,
        fill: active ? '#fff' : '#666',
        textAlign: 'left',
        textBaseline: 'middle',
        listening: false,
    }));
    setState(group, { label: labelText, active: active, width: bounds.width, height: bounds.height });
    return group;
});
registerAutomotive('adasStatus', function (props, app) {
    var _a, _b, _c;
    var status = str(props, 'status', 'off');
    var colors = { off: '#333', standby: '#f59e0b', active: '#22c55e', fault: '#ef4444' };
    var bounds = resolveBounds(props, 96, 28);
    var group = createAutoGroup(app, 'adasStatus', __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), 'adasStatus');
    var w = bounds.innerWidth;
    var h = bounds.innerHeight;
    var compact = w < 56;
    var label = compact ? "ADAS ".concat(status === 'off' ? '—' : (_b = (_a = status[0]) === null || _a === void 0 ? void 0 : _a.toUpperCase()) !== null && _b !== void 0 ? _b : '?') : "ADAS ".concat(status.toUpperCase());
    group.add(app.rect({ width: w, height: h, fill: (_c = colors[status]) !== null && _c !== void 0 ? _c : '#333', cornerRadius: 4, listening: false }), autoCenteredText(app, label, w, h / 2, {
        fontSize: fluidFont(10, bounds, 7, 11),
        fill: '#fff',
    }));
    group.metadata.textRefresh = function (t) {
        var _a;
        var bg = group.children[0];
        if (bg)
            bg.fill = (_a = colors[t.toLowerCase()]) !== null && _a !== void 0 ? _a : '#333';
    };
    setState(group, { status: status, width: bounds.width, height: bounds.height });
    return group;
});
/** Pedestrian detection ADAS badge — clear / detecting / warning. */
registerAutomotive('pedestrianDetection', function (props, app) {
    var _a, _b, _c;
    var status = str(props, 'status', str(props, 'text', 'clear')).toLowerCase();
    var colors = {
        clear: '#333',
        off: '#333',
        detecting: '#2563eb',
        standby: '#f59e0b',
        warning: '#ef4444',
        alert: '#ef4444',
        fault: '#ef4444',
    };
    var bounds = resolveBounds(props, 120, 36);
    var group = createAutoGroup(app, 'pedestrianDetection', __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), 'pedestrianDetection');
    var w = bounds.innerWidth;
    var h = bounds.innerHeight;
    var compact = w < 72;
    var label = compact
        ? "PED ".concat(status === 'clear' || status === 'off' ? '—' : (_b = (_a = status[0]) === null || _a === void 0 ? void 0 : _a.toUpperCase()) !== null && _b !== void 0 ? _b : '?')
        : "PED ".concat(status.toUpperCase());
    var bg = app.roundedRect({
        width: w,
        height: h,
        cornerRadius: Math.min(6, h * 0.2),
        fill: (_c = colors[status]) !== null && _c !== void 0 ? _c : '#333',
        listening: false,
    });
    var text = autoCenteredText(app, label, w, h / 2, {
        fontSize: fluidFont(10, bounds, 7, 11),
        fontWeight: 'bold',
        fill: '#fff',
    });
    group.add(bg, text);
    group.metadata.textRefresh = function (t) {
        var _a, _b, _c;
        var key = t.toLowerCase();
        bg.fill = (_a = colors[key]) !== null && _a !== void 0 ? _a : '#333';
        text.text = compact
            ? "PED ".concat(key === 'clear' || key === 'off' ? '—' : (_c = (_b = key[0]) === null || _b === void 0 ? void 0 : _b.toUpperCase()) !== null && _c !== void 0 ? _c : '?')
            : "PED ".concat(key.toUpperCase());
    };
    setState(group, { status: status, width: bounds.width, height: bounds.height });
    return group;
});
